/**
 * Fuzzy matching for the command bar. Pure so it can be tested. Scores a query against a title plus keywords, with
 * a synonym table so "write" finds "Add a note" and "dark" finds the theme switch.
 */
export const SYNONYMS: Record<string, string[]> = {
  note: ["write", "jot", "memo", "remember"],
  reminder: ["remind", "follow up", "followup", "todo", "later", "ping"],
  group: ["label", "tag", "list", "collection"],
  person: ["contact", "people", "someone", "human"],
  create: ["new", "add", "make"],
  archive: ["hide", "remove", "bin"],
  settings: ["preferences", "options", "config"],
  theme: ["dark", "light", "appearance", "mode"],
  search: ["find", "look", "query"],
  home: ["feed", "dashboard", "start"],
  cadence: ["reconnect", "frequency", "keep in touch", "stay in touch"],
  star: ["favorite", "favourite", "pin"],
  merge: ["duplicate", "combine", "dedupe"],
  need: ["ask", "looking for", "want"],
  service: ["offer", "provide", "skill"],
  matches: ["introductions", "intros"],
  shortcuts: ["keyboard", "keys", "hotkeys"],
};

function expand(query: string): string[] {
  const q = query.toLowerCase().trim();
  const words = q.split(/\s+/).filter(Boolean);
  const out = new Set<string>(words);
  for (const [canon, syns] of Object.entries(SYNONYMS)) {
    for (const w of words) {
      if (w === canon || syns.some((s) => s === w || s.startsWith(w) || w.startsWith(s))) {
        out.add(canon);
        syns.forEach((s) => out.add(s));
      }
    }
  }
  return Array.from(out);
}

/** Subsequence match: every character of `needle` appears in order in `hay`. Returns a 0..1 quality score or 0. */
export function subsequenceScore(needle: string, hay: string): number {
  if (!needle) return 0.01;
  let hi = 0;
  let streak = 0;
  let score = 0;
  for (const ch of needle) {
    const idx = hay.indexOf(ch, hi);
    if (idx < 0) return 0;
    streak = idx === hi ? streak + 1 : 1;
    score += streak;
    hi = idx + 1;
  }
  return Math.min(1, score / (needle.length * 2)) * (needle.length / Math.max(needle.length, hay.length * 0.4));
}

export function scoreItem(query: string, title: string, keywords: string[] = []): number {
  const q = query.toLowerCase().trim();
  if (!q) return 1;
  const t = title.toLowerCase();
  if (t === q) return 10;
  if (t.startsWith(q)) return 8;
  if (t.includes(q)) return 6;
  const terms = expand(q);
  const kw = keywords.map((k) => k.toLowerCase());
  let best = 0;
  for (const term of terms) {
    if (t.includes(term)) best = Math.max(best, 4 + term.length / 20);
    for (const k of kw) {
      if (k === term) best = Math.max(best, 4.5);
      else if (k.includes(term) || term.includes(k)) best = Math.max(best, 3);
    }
  }
  if (best > 0) return best;
  // Every query word must appear somewhere, in any order.
  const words = q.split(/\s+/).filter(Boolean);
  if (words.length > 1 && words.every((w) => t.includes(w) || kw.some((k) => k.includes(w)))) return 2.5;
  const sub = subsequenceScore(q.replace(/\s+/g, ""), t.replace(/\s+/g, ""));
  return sub > 0.3 ? sub * 2 : 0;
}

export function rankWithScores<T extends { title: string; keywords?: string[] }>(query: string, items: T[], limit = 40): { item: T; score: number }[] {
  return items
    .map((item) => ({ item, score: scoreItem(query, item.title, item.keywords ?? []) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

export function rank<T extends { title: string; keywords?: string[] }>(query: string, items: T[], limit = 40): T[] {
  return rankWithScores(query, items, limit).map((x) => x.item);
}

/** A direct or synonym hit on a command outranks free-text people results. */
export const STRONG_MATCH = 4;
